import * as Sentry from '@sentry/react';
import {
  useUser,
  useClerk,
  UserButton,
  OrganizationSwitcher,
} from '@clerk/react';
import { Link, useLocation } from 'react-router-dom';
import React, { Fragment, useEffect, useState, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XIcon, MenuAlt2Icon } from '@heroicons/react/outline';
import {
  IoBugOutline,
  IoHelpCircleOutline,
  IoCallOutline,
} from 'react-icons/io5';
import { usePostHog } from 'posthog-js/react';

import {
  MenuItem,
  getToolItems,
  getDeveloperItems,
  getInsuranceItems,
  getDashboardItems,
  getNavigationTramitItems,
  getNavigationCompanyItems,
} from './navigation-items';
import Searchbox from './components/Searchbox/Searchbox';
import NavigationItem from './NavigationItem';
import TramitesNav from './TramitesNav';
import Banner from './components/Banners';
import CallSupportModal from '../modals/CallSupportModal/CallSupportModal';
import { userInfoContext } from '../context/UserInfoContext/UserInfoContext';

Sentry.init({
  environment:
    process.env.REACT_APP_ENV === 'PROD' ? 'production' : 'development',
  dsn: 'https://71732caa56bf577d804396e72b2a11ef@o1195451.ingest.us.sentry.io/4509927619428352',
  tracesSampleRate: 0.1,
  tracePropagationTargets: [
    'https://backoffice.swipoo.com',
    'https://sandbox.backoffice-1no.pages.dev',
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.feedbackIntegration({
      autoInject: false,
      colorScheme: 'light',
      showBranding: false,
      formTitle: 'Reportar un bug',
      cancelButtonLabel: 'Cancelar',
      submitButtonLabel: 'Enviar',
      showName: false,
      showEmail: false,
      messageLabel: 'Descripción',
      isRequiredLabel: '',
      messagePlaceholder: '¿Qué ha ido mal? ¿Qué esperabas que sucediera?',
      addScreenshotButtonLabel: 'Añadir captura de pantalla',
      removeScreenshotButtonLabel: 'Eliminar captura de pantalla',
      successMessageText: '¡Gracias por tu feedback!',
      themeLight: {
        accentBackground: '#235EDF',
      },
      useSentryUser: {
        name: 'fullName',
        email: 'email',
      },
    }),
  ],
});

const orgSwitcherAppearance = {
  variables: {
    colorPrimary: '#2A64DF',
  },
  elements: {
    rootBox: 'w-full',
    organizationSwitcherTrigger:
      'w-full justify-between gap-2 px-2 py-2 rounded-md hover:bg-gray-200 focus:shadow-none',
  },
};

function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [callModalOpen, setCallModalOpen] = useState<boolean>(false);

  const location = useLocation();

  const { user } = useUser();
  const clerk = useClerk();
  const userEmail = user?.primaryEmailAddress?.emailAddress;
  const posthogClient = usePostHog();
  const { accountInfo } = useContext(userInfoContext);

  const dashboardItems = getDashboardItems(location);
  const tramitItems = getNavigationTramitItems(location);
  const companyItems = getNavigationCompanyItems(location);
  const insuranceItems = getInsuranceItems(location);
  const toolItems = getToolItems(location);
  const developerItems = getDeveloperItems(location);

  const toggleSleekHelpcenter = () => {
    if (window && window.$sleek) {
      window.$sleek.toggle();
    }
  };

  const toggeReportBug = async () => {
    const feedback = Sentry.getFeedback();
    const form = await feedback?.createForm();
    form?.appendToDom();
    form?.open();
  };

  const openCallModal = () => {
    setCallModalOpen(true);
  };

  useEffect(() => {
    const unsubscribe = clerk.addListener(
      ({ user: emittedUser }: { user?: unknown }) => {
        if (emittedUser) return;
        if (window && window.$sleek) {
          window.$sleek.shutdown();
        }
        if (posthogClient) {
          posthogClient.reset();
        }
      },
      { skipInitialEmit: true },
    );
    return unsubscribe;
  }, [clerk, posthogClient]);

  useEffect(() => {
    if (!user) return;
    if (window && window.$sleek) {
      window.$sleek.setUser({
        mail: userEmail,
        id: user.id,
        name: user.fullName,
        img: user.imageUrl,
      });
    }
    if (posthogClient && accountInfo) {
      posthogClient.identify(user.id, {
        name: user.fullName,
        email: userEmail,
      });
      posthogClient.group('company', accountInfo.id, {
        name: accountInfo.name,
      });
    }
    Sentry.setUser({
      fullName: user.fullName,
      email: userEmail,
    });
  }, [accountInfo, user]);

  return (
    <div className="relative h-full">
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-40 lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600/75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-gray-800">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Cerrar sidebar</span>
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <Link to="/">
                <div className="shrink-0 flex items-center px-4">
                  <img
                    src="https://s3.eu-west-1.amazonaws.com/assets.swipoo.com/swipoo-white-icon.svg"
                    alt="swipoo logo"
                    width="35"
                    height="35"
                  />
                  <span className="ml-4">
                    <img
                      src="https://s3.eu-west-1.amazonaws.com/assets.swipoo.com/swipoo-white-name.svg"
                      alt="swipoo name"
                      width="75"
                      height="70"
                    />
                  </span>
                </div>
              </Link>
              <div className="mt-5 flex-1 h-0 overflow-y-auto">
                <nav className="px-2 space-y-1">
                  {dashboardItems.map((item: MenuItem) => (
                    <NavigationItem
                      item={item}
                      textColorClass="text-gray-200"
                      key={item.name}
                    />
                  ))}
                  <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                    TRAMITES
                  </div>
                  <TramitesNav
                    items={tramitItems}
                    textColorClass="text-gray-200"
                  />
                  <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                    SEGUROS
                  </div>
                  {insuranceItems.map((item: MenuItem) => (
                    <NavigationItem
                      item={item}
                      textColorClass="text-gray-200"
                      key={item.name}
                    />
                  ))}
                  <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                    HERRAMIENTAS
                  </div>
                  {toolItems.map((item: MenuItem) => (
                    <NavigationItem
                      item={item}
                      textColorClass="text-gray-200"
                      key={item.name}
                    />
                  ))}
                  <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                    TU EMPRESA
                  </div>
                  {companyItems.map((item: MenuItem) => (
                    <NavigationItem
                      item={item}
                      textColorClass="text-gray-200"
                      key={item.name}
                    />
                  ))}
                  <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                    DESARROLLADORES
                  </div>
                  {developerItems.map((item: MenuItem) => (
                    <NavigationItem
                      item={item}
                      textColorClass="text-gray-200"
                      key={item.name}
                    />
                  ))}
                </nav>
              </div>
              <div className="shrink-0 border-t border-r border-gray-200 p-3">
                <OrganizationSwitcher
                  hidePersonal
                  afterSelectOrganizationUrl="/"
                  afterCreateOrganizationUrl="/"
                  appearance={orgSwitcherAppearance}
                />
              </div>
            </div>
          </Transition.Child>
          <div className="shrink-0 w-14" aria-hidden="true">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </Dialog>
      </Transition.Root>
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-gray-100">
          <Link to="/">
            <div className="flex items-center h-16 shrink-0 px-4 bg-topbar">
              <img
                src="https://s3.eu-west-1.amazonaws.com/assets.swipoo.com/swipoo-white-icon.svg"
                alt="swipoo logo"
                width="35"
                height="35"
              />
              <span className="ml-4">
                <img
                  src="https://s3.eu-west-1.amazonaws.com/assets.swipoo.com/swipoo-white-name.svg"
                  alt="swipoo logo"
                  width="75"
                  height="65"
                />
              </span>
            </div>
          </Link>
          <div className="flex-1 flex flex-col overflow-y-auto border-r border-r-gray-200">
            <nav className="flex-1 px-2 py-4 space-y-1">
              {dashboardItems.map((item: MenuItem) => (
                <NavigationItem item={item} key={item.name} />
              ))}
              <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                TRAMITES
              </div>
              <TramitesNav items={tramitItems} />
              <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                SEGUROS
              </div>
              {insuranceItems.map((item: MenuItem) => (
                <NavigationItem item={item} key={item.name} />
              ))}
              <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                HERRAMIENTAS
              </div>
              {toolItems.map((item: MenuItem) => (
                <NavigationItem item={item} key={item.name} />
              ))}
              <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                TU EMPRESA
              </div>
              {companyItems.map((item: MenuItem) => (
                <NavigationItem item={item} key={item.name} />
              ))}
              <div className="ml-2 text-xs font-semibold leading-6 text-gray-400">
                DESARROLLADORES
              </div>
              {developerItems.map((item: MenuItem) => (
                <NavigationItem item={item} key={item.name} />
              ))}
            </nav>
          </div>
          <div className="shrink-0 border-t border-r border-gray-200 p-3">
            <OrganizationSwitcher
              hidePersonal
              afterSelectOrganizationUrl="/"
              afterCreateOrganizationUrl="/"
              appearance={orgSwitcherAppearance}
            />
          </div>
        </div>
      </div>
      <div className="lg:pl-64 flex flex-col h-full w-full pt-16">
        <div className="fixed top-0 inset-x-0 lg:left-64 z-10 flex gap-2 items-center justify-between h-16 bg-topbar">
          <button
            type="button"
            className="p-4 text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Abrir sidebar</span>
            <MenuAlt2Icon className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="w-full flex justify-center">
            <Searchbox />
          </div>
          <div className="flex items-center gap-4 p-6">
            <button
              type="button"
              className="max-w-xs rounded-full flex items-center text-sm focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-white lg:rounded-md"
              onClick={toggeReportBug}
            >
              <IoBugOutline className="h-6 w-6 text-gray-200 hover:text-white" />
            </button>
            <button
              type="button"
              className="max-w-xs rounded-full flex items-center text-sm focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-white lg:rounded-md"
              onClick={openCallModal}
            >
              <IoCallOutline className="h-6 w-6 text-gray-200 hover:text-white" />
            </button>
            <button
              type="button"
              className="max-w-xs rounded-full flex items-center text-sm focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-white lg:rounded-md"
              onClick={toggleSleekHelpcenter}
            >
              <IoHelpCircleOutline className="h-8 w-8 text-gray-200 hover:text-white" />
            </button>
            <UserButton />
          </div>
        </div>
        <main className="h-full bg-gray-100">{children}</main>
        <Banner />
      </div>
      <CallSupportModal open={callModalOpen} setOpen={setCallModalOpen} />
    </div>
  );
}

export default Layout;
